<template>
  <div :class="{ 'master-detail': true, 'master-detail--show-detail': showDetail }">
    <MobileHeader :label="pageTitle">
      <div class="menu">
        <ul class="menu__items">
          <li class="menu-item">
            <button
              class="menu-item__link menu-item__link--icon"
              type="button"
              @click="openMobileSearch"
            >
              <div class="menu-item__label">
                <UseSvg
                  :aria-label="$t('general.search')"
                  class="icon"
                  name="search"
                />
              </div>
            </button>
          </li>
          <slot name="mobileActions" />
        </ul>
      </div>
    </MobileHeader>

    <div class="master-detail__master">
      <div
        v-if="pageTitle"
        class="master-detail__page-title typo-title"
      >
        {{ pageTitle }}
      </div>
      <div
        v-if="hasInspection"
        :class="{
          'mobile-overlay': true,
          'show-for-medium': !mobileSearchOpen || showDetail,
          search: search,
        }"
      >
        <div
          :class="{
            'form form--flat': true,
            'form--separated': !mobileSearchOpen,
          }"
        >
          <div class="form__row">
            <div class="input input--light input--flat input--search">
              <div
                :class="{
                  input__prefix: true,
                  'input__prefix--hilight': search,
                }"
              >
                <UseSvg
                  class="icon"
                  name="search"
                />
              </div>
              <input
                ref="searchInput"
                v-model="search"
                :aria-label="searchPlaceholder"
                :placeholder="searchPlaceholder"
              >
              <button
                v-if="search || mobileSearchOpen"
                class="input__clear"
                type="button"
                @click="clearSearch"
              >
                <UseSvg
                  :aria-label="$t('general.close_search')"
                  :class="{ 'icon': true, 'icon--small': !mobileSearchOpen }"
                  :name="mobileSearchOpen ? 'close_circle_24' : 'close_circle_16'"
                />
              </button>
            </div>
          </div>
        </div>

        <template v-if="search && searchResults">
          <HeaderBar :modifiers="['narrow', 'darkest', 'transparent-on-medium']">
            <span class="typo-small">{{ $t('general.results') }}</span>
          </HeaderBar>
          <div
            v-if="searchResults.length > 0"
            class="scroll-container scroll-container--padded"
          >
            <template
              v-for="item in searchResults"
              :key="item[itemKey]"
            >
              <slot
                :item="item"
                name="item"
                @click.passive="selectSearchedItem"
              />
            </template>
          </div>
          <EmptyMessage
            v-else
            :message="$t('general.no_results')"
          >
            <UseSvg name="magnifying_glass_circle" />
          </EmptyMessage>
        </template>
        <div v-else /> <!-- HACK: enforce "form--separated" still does the trick -->
      </div>

      <template v-if="!search">
        <HeaderBar
          v-if="hasInspection"
          :modifiers="['narrow', 'darkest', 'sticky-with-nav', 'transparent-on-medium']"
        >
          <div class="menu">
            <ul
              v-if="!loading"
              class="menu__items"
            >
              <li
                v-for="tab in tabs"
                :key="tab.label"
                class="menu-item typo-small"
              >
                <div class="menu-item__link menu-item__link--icon">
                  <router-link
                    activeClass="menu-item__link--active menu-item__link--disabled"
                    class="menu-item__link menu-item__link--icon"
                    :to="tab.to"
                  >
                    <span class="menu-item__label">{{ tab.count }} {{ tab.label }}</span>
                  </router-link>
                </div>
              </li>
            </ul>
          </div>

          <template #right>
            <Select
              v-if="items.length > 1 && sortByOptions"
              v-model="sortBy"
              :items="sortByOptions"
              :placeholder="$t('general.sort_by')"
            />
          </template>
        </HeaderBar>
        <div
          v-if="!loading && inspections?.length > 0"
          ref="container"
          class="scroll-container scroll-container--padded"
        >
          <template
            v-for="item in inspections"
            :key="item[itemKey]"
          >
            <slot
              :item="item"
              name="item"
            />
          </template>
        </div>
        <EmptyMessage
          v-if="!loading && items.length === 0"
          :message="$t('operations.create_new_inspection_message')"
          :modifiers="['grow', 'center']"
        >
          <CirclePointTargetSvg class="icon" />
          <template #actions>
            <button
              class="button button--inverse"
              type="button"
              @click="$emit('newInspection')"
            >
              <div class="button__icon">
                <PlusIcon />
              </div>
              <div class="button__label">
                {{ $t('inspections.new_inspection') }}
              </div>
            </button>
          </template>
        </EmptyMessage>
      </template>
      <Loader v-if="loading" />
      <slot
        v-if="items.length !== 0"
        name="actions"
      />
    </div>
    <div class="master-detail__detail">
      <slot
        v-if="$route.params.inspectionId"
        name="detail"
      />
      <EmptyMessage
        v-else-if="items?.length !== 0"
        :message="$t('operations.select_an_inspection_to_view_issues')"
        :modifiers="['grow', 'center']"
      >
        <CircleSquareTarget class="icon" />
      </EmptyMessage>
      <EmptyMessage
        v-else
        :message="$t('operations.inspection_issues_will_appear_here')"
        :modifiers="['grow', 'center']"
      >
        <CircleSquareTarget class="icon" />
      </EmptyMessage>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, computed } from 'vue';
import { useRoute } from 'vue-router';
import { onBeforeRouteUpdate } from 'vue-router';

// Utils
import onEscapePressed from '@/functions/on_escape_pressed';
import Searcher from '@/functions/searcher';

// Components
import EmptyMessage from '@/components/generic/empty_message.vue';
import MobileHeader from '@/components/generic/mobile_header.vue';
import Loader from '@/components/generic/loader.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import Select from '@/components/form/select.vue';
import UseSvg from '@/components/generic/use_svg.vue';

// Assets
import CirclePointTargetSvg from '@/images/circle-point-target.svg';
import CircleSquareTarget from '@/images/circle-square-target.svg';
import PlusIcon from '@/images/plus-16.svg';

const props = defineProps({
  items: { type: Array, required: true },
  itemKey: { type: String, default: 'id' },
  pageTitle: { type: String, required: true },
  searchKeys: { type: Array, required: true },
  searchPlaceholder: { type: String, required: true },
  emptyStateTitle: { type: String, required: true },
  emptyStateMessage: { type: String, required: true },
  showDetail: Boolean,
  tabs: { type: Array, default: null },
  sortByOptions: { type: Array, default: null },
  loading: Boolean,
});

defineEmits(['newInspection']);

const route = useRoute();
const container = ref();
const sortBy = ref(null);
const search = ref('');
const searchResults = ref(null);
const searchInput = ref(null);
const mobileSearchOpen = ref(false);

const inspections = ref(null);

let searcher;

onMounted(() => buildSearcher());

onBeforeRouteUpdate(() => {
  if (mobileSearchOpen.value) clearSearch();
});

onEscapePressed(clearSearch);

const hasInspection = computed(() => (props.tabs[0].count > 0 || props.tabs[1].count > 0));

watch(() => props.items, buildSearcher);

/**
 * Watches for changes on the sort by dropdown
 */
watch(sortBy, () => {
  if (container.value) {
    container.value.scrollTo({ top: 0, behavior: 'smooth' });
  }

  if (sortBy.value === 'newest') {
    inspections.value.sort(function (a, b) {
      return new Date(b.date) - new Date(a.date);
    });
  } else {
    inspections.value.sort(function (a, b) {
      return new Date(a.date) - new Date(b.date);
    });
  }
});

/**
 * Resets sort label text
 */
watch(() => route.params.scope, () => sortBy.value = null);

watch(search, () => {
  if (search.value) {
    searchResults.value = searcher.search(search.value);
  } else {
    searchResults.value = null;
  }
});

function buildSearcher(items = props.items) {
  searcher = new Searcher(items, { keys: props.searchKeys });
  inspections.value = props.items;
}

async function openMobileSearch() {
  search.value = '';
  mobileSearchOpen.value = true;

  await nextTick();
  searchInput.value.focus();
}

function clearSearch() {
  search.value = '';
  mobileSearchOpen.value = false;
}

function selectSearchedItem() {
  if (!mobileSearchOpen.value) clearSearch();
}
</script>

<style lang="scss" scoped>
@import '@/stylesheets/global';

.search {
  flex-grow: 1;
  @include breakpoint(medium) {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
}

</style>
